import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import GridLayout from "react-grid-layout";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import {
  getSelectedDashboard,
  getSelectedDashboardTileIds,
  saveDashboardTileLayouts,
} from "../../store/conversation.tsx";
import { DashboardTile } from "../../store/types.tsx";
import DashboardTileCard from "./DashboardTileCard.tsx";
import DashboardTilePlaceholderCard from "./DashboardTilePlaceholderCard.tsx";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "../../styles/dashboard-grid.css";

const DESKTOP_DASHBOARD_COLUMNS = 12;
const DESKTOP_ROW_HEIGHT = 28;
const DESKTOP_GRID_MARGIN = 18;

type GridLayoutItem = {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
};

const getTileSizeConstraints = (type?: string) => {
  if (type === "Graph") {
    return { w: 6, h: 12, minW: 4, minH: 9 };
  }

  if (type === "Table") {
    return { w: 8, h: 12, minW: 5, minH: 9 };
  }

  return { w: 4, h: 7, minW: 3, minH: 5 };
};

const normalizeGridLayout = (
  layout: Array<Pick<GridLayoutItem, "i" | "x" | "y" | "w" | "h">>,
  tileTypesById: Record<string, string>,
): GridLayoutItem[] =>
  layout.map((item) => ({
    ...item,
    ...getTileSizeConstraints(tileTypesById[item.i]),
    x: item.x,
    y: item.y,
    w: item.w,
    h: item.h,
  }));

const collides = (
  left: Pick<GridLayoutItem, "x" | "y" | "w" | "h">,
  right: Pick<GridLayoutItem, "x" | "y" | "w" | "h">,
) =>
  left.x < right.x + right.w &&
  left.x + left.w > right.x &&
  left.y < right.y + right.h &&
  left.y + left.h > right.y;

const findOpenPosition = (
  width: number,
  height: number,
  placedTiles: GridLayoutItem[],
) => {
  let y = 0;

  while (y < 1000) {
    for (let x = 0; x <= DESKTOP_DASHBOARD_COLUMNS - width; x += 1) {
      const candidate = { x, y, w: width, h: height };
      const hasCollision = placedTiles.some((placedTile) =>
        collides(candidate, placedTile),
      );
      if (!hasCollision) {
        return { x, y };
      }
    }
    y += 1;
  }

  return { x: 0, y };
};

const buildGridLayout = (
  tileIds: string[],
  tileTypesById: Record<string, string>,
  tileLayoutsById: Record<string, DashboardTile["layout"]>,
): GridLayoutItem[] => {
  const placedTiles: GridLayoutItem[] = [];

  return tileIds.map((tileId) => {
    const savedLayout = tileLayoutsById[tileId];
    if (savedLayout) {
      const defaults = getTileSizeConstraints(tileTypesById[tileId]);
      const item = {
        i: tileId,
        x: savedLayout.column,
        y: savedLayout.row,
        w: savedLayout.column_span,
        h: savedLayout.row_span,
        minW: defaults.minW,
        minH: defaults.minH,
      };
      placedTiles.push(item);
      return item;
    }

    const defaults = getTileSizeConstraints(tileTypesById[tileId]);
    const openPosition = findOpenPosition(defaults.w, defaults.h, placedTiles);

    const item: GridLayoutItem = {
      i: tileId,
      x: openPosition.x,
      y: openPosition.y,
      ...defaults,
    };

    placedTiles.push(item);
    return item;
  });
};

const sortTileIdsByLayout = (
  tileIds: string[],
  tileLayoutsById: Record<string, DashboardTile["layout"]>,
) => {
  const tileOrder = tileIds.reduce(
    (accumulator, tileId, index) => {
      accumulator[tileId] = index;
      return accumulator;
    },
    {} as Record<string, number>,
  );

  return [...tileIds].sort((leftId, rightId) => {
    const left = tileLayoutsById[leftId];
    const right = tileLayoutsById[rightId];

    if (!left && !right) {
      return tileOrder[leftId] - tileOrder[rightId];
    }
    if (!left) {
      return 1;
    }
    if (!right) {
      return -1;
    }
    if (left.row !== right.row) {
      return left.row - right.row;
    }
    if (left.column !== right.column) {
      return left.column - right.column;
    }
    return tileOrder[leftId] - tileOrder[rightId];
  });
};

const hasUnsavedLayoutChanges = (
  layout: GridLayoutItem[],
  tileLayoutsById: Record<string, DashboardTile["layout"]>,
) =>
  layout.some((item) => {
    const savedLayout = tileLayoutsById[item.i];
    return (
      !savedLayout ||
      savedLayout.column !== item.x ||
      savedLayout.row !== item.y ||
      savedLayout.column_span !== item.w ||
      savedLayout.row_span !== item.h
    );
  });

const useGridWidth = () => {
  const [containerNode, setContainerNode] =
    React.useState<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState<number | null>(null);

  const containerRef = React.useCallback((node: HTMLDivElement | null) => {
    setContainerNode(node);
  }, []);

  React.useLayoutEffect(() => {
    if (!containerNode || typeof window === "undefined") {
      return;
    }

    const measure = () => {
      if (!containerNode) {
        return;
      }

      const nextWidth = Math.round(containerNode.getBoundingClientRect().width);
      if (nextWidth > 0) {
        setWidth(nextWidth);
      }
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => {
        measure();
      });
      observer.observe(containerNode);

      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [containerNode]);

  return { containerRef, width };
};

const DashboardView: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const dashboard = useTypedSelector((state) => getSelectedDashboard(state));
  const tileIds = useTypedSelector((state) =>
    getSelectedDashboardTileIds(state),
  );
  const dashboardTilesById = useTypedSelector(
    (state) => state.conversationState.dashboardTilesById,
  );
  const dashboardTileTypesById = useTypedSelector(
    (state) => state.conversationState.dashboardTileTypesById,
  );
  const dashboardTileLayoutsById = useTypedSelector(
    (state) => state.conversationState.dashboardTileLayoutsById,
  );
  const isLoading = useTypedSelector((state) =>
    dashboard
      ? state.conversationState.dashboardLoadingById[dashboard.id]
      : false,
  );
  const { containerRef: gridContainerRef, width: gridWidth } = useGridWidth();
  const persistedLayoutsByTileId = React.useMemo(
    () =>
      tileIds.reduce(
        (accumulator, tileId) => {
          accumulator[tileId] =
            dashboardTileLayoutsById[tileId] ||
            dashboardTilesById[tileId]?.layout ||
            null;
          return accumulator;
        },
        {} as Record<string, DashboardTile["layout"]>,
      ),
    [tileIds, dashboardTileLayoutsById, dashboardTilesById],
  );

  const orderedTileIds = React.useMemo(
    () => sortTileIdsByLayout(tileIds, persistedLayoutsByTileId),
    [tileIds, persistedLayoutsByTileId],
  );

  const persistedGridLayout = React.useMemo(
    () =>
      buildGridLayout(
        orderedTileIds,
        dashboardTileTypesById,
        persistedLayoutsByTileId,
      ),
    [orderedTileIds, persistedLayoutsByTileId, dashboardTileTypesById],
  );
  const [draftLayout, setDraftLayout] =
    React.useState<GridLayoutItem[]>(persistedGridLayout);
  const [isSavingLayout, setIsSavingLayout] = React.useState(false);

  React.useEffect(() => {
    setDraftLayout(persistedGridLayout);
  }, [dashboard?.id, persistedGridLayout]);

  const handleLayoutChange = React.useCallback(
    (nextLayout: GridLayoutItem[]) => {
      setDraftLayout(normalizeGridLayout(nextLayout, dashboardTileTypesById));
    },
    [dashboardTileTypesById],
  );

  const layoutHasUnsavedChanges = React.useMemo(
    () => hasUnsavedLayoutChanges(draftLayout, persistedLayoutsByTileId),
    [draftLayout, persistedLayoutsByTileId],
  );

  const handleSaveLayout = React.useCallback(async () => {
    if (!dashboard || !layoutHasUnsavedChanges) {
      return;
    }

    setIsSavingLayout(true);
    try {
      const normalizedLayout = normalizeGridLayout(
        draftLayout,
        dashboardTileTypesById,
      );
      setDraftLayout(normalizedLayout);
      await (dispatch(
        saveDashboardTileLayouts({
          dashboardId: dashboard.id,
          layouts: normalizedLayout.map((item) => ({
            id: item.i,
            column: item.x,
            row: item.y,
            column_span: item.w,
            row_span: item.h,
          })),
        }) as any,
      ) as Promise<void>);
    } finally {
      setIsSavingLayout(false);
    }
  }, [
    dashboard,
    dashboardTileTypesById,
    draftLayout,
    dispatch,
    layoutHasUnsavedChanges,
  ]);

  if (!dashboard) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", paddingBottom: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.25,
          marginBottom: 2.5,
          px: { xs: 2, md: 2.5 },
          py: 1.75,
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.52)",
          border: "1px solid rgba(169,125,88,0.32)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(169, 125, 88, 0.9)",
              flexShrink: 0,
            }}
          >
            <DashboardCustomizeOutlinedIcon
              sx={{ color: "rgba(47, 33, 24, 0.92)", fontSize: 18 }}
            />
          </Box>
          <Typography variant="h4">{dashboard.title}</Typography>
        </Box>
        {isDesktop && layoutHasUnsavedChanges && (
          <Button
            variant="outlined"
            onClick={handleSaveLayout}
            disabled={isSavingLayout}
            sx={{
              marginLeft: "auto",
              minWidth: 128,
              borderRadius: 999,
              px: 2.2,
              py: 1,
              color: theme.palette.primary.main,
              backgroundColor: "rgba(255,255,255,0.28)",
              borderColor: "rgba(169,125,88,0.55)",
              transition:
                "background-color 160ms ease, border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease",
              "&:hover": {
                borderColor: "rgba(169,125,88,0.8)",
                backgroundColor: "rgba(255,255,255,0.44)",
                boxShadow: "0 8px 18px rgba(169,125,88,0.16)",
                transform: "translateY(-1px)",
              },
              "&.Mui-disabled": {
                color: "rgba(47, 33, 24, 0.55)",
                borderColor: "rgba(169,125,88,0.28)",
                backgroundColor: "rgba(255,255,255,0.18)",
              },
            }}
          >
            {isSavingLayout ? i18n._(t`Saving...`) : i18n._(t`Save layout`)}
          </Button>
        )}
      </Box>

      {isLoading && tileIds.length === 0 ? (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "rgba(47, 33, 24, 0.78)",
          }}
        >
          <CircularProgress size={34} thickness={4.5} />
          <Typography variant="body1">
            {i18n._(t`Loading dashboard tiles...`)}
          </Typography>
        </Box>
      ) : tileIds.length === 0 ? (
        <Box
          sx={{
            borderRadius: 4,
            padding: 4,
            border: "1px dashed rgba(169,125,88,0.55)",
            background: "rgba(255,255,255,0.72)",
          }}
        >
          <Typography variant="body1">
            {i18n._(
              t`Save answers from your conversations to surface them here.`,
            )}
          </Typography>
        </Box>
      ) : isDesktop ? (
        <Box ref={gridContainerRef} sx={{ width: "100%" }}>
          {gridWidth !== null && (
            <GridLayout
              className="dashboard-grid-layout"
              layout={draftLayout}
              width={gridWidth}
              cols={DESKTOP_DASHBOARD_COLUMNS}
              rowHeight={DESKTOP_ROW_HEIGHT}
              margin={[DESKTOP_GRID_MARGIN, DESKTOP_GRID_MARGIN]}
              containerPadding={[0, 0]}
              compactType={null}
              preventCollision
              isBounded
              draggableHandle=".dashboard-tile-drag-handle"
              draggableCancel=".dashboard-tile-action, .react-resizable-handle"
              resizeHandles={["se"]}
              onLayoutChange={handleLayoutChange}
            >
              {orderedTileIds.map((tileId) => {
                const tile = dashboardTilesById[tileId];
                if (!tile) {
                  return (
                    <div key={tileId} className="dashboard-grid-item">
                      <DashboardTilePlaceholderCard
                        type={dashboardTileTypesById[tileId]}
                        fillHeight
                      />
                    </div>
                  );
                }

                return (
                  <div key={tileId} className="dashboard-grid-item">
                    <DashboardTileCard tile={tile} fillHeight />
                  </div>
                );
              })}
            </GridLayout>
          )}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
          {orderedTileIds.map((tileId) => {
            const tile = dashboardTilesById[tileId];
            if (!tile) {
              return (
                <DashboardTilePlaceholderCard
                  key={tileId}
                  type={dashboardTileTypesById[tileId]}
                />
              );
            }

            return <DashboardTileCard key={tileId} tile={tile} />;
          })}
        </Box>
      )}
    </Box>
  );
};

export default DashboardView;
